/**
 * Live chat in the console: the chat writes (they ride the Support inbox
 * write gate, `worker/support-inbox-writes.ts`), the staff socket's wire
 * shapes, and the small pure rules the screens share (who owns a chat, when a
 * chat should alert, how a live row joins the page, business hours as text).
 *
 * The socket frames mirror nt-support's `src/chat/protocol.ts`; only the
 * fields the console reads are declared.
 */
import { postSupportInboxWrite } from "@/lib/support-inbox";
import type {
  ChatAvailabilityView,
  ChatHistoryItem,
  ChatHistoryPage,
  ChatOwner,
  ChatSettings,
  ChatSettingsView,
  ChatStaffAvailability,
  ChatWeekday,
  TicketDetail,
  TicketStatus,
  TicketSummary,
} from "@/types-support";

// --- writes -----------------------------------------------------------------

function chatTicketPath(id: string, action: string): string {
  return `/api/admin/support/inbox/${encodeURIComponent(id)}/${action}`;
}

/**
 * A chat reply: the ticket reply route, which nt-support sends to the chat.
 * The chat stays open while it is live; Solve ends it.
 */
export function replyToChat(
  id: string,
  body: string,
  idempotencyKey: string,
): Promise<TicketDetail> {
  return postSupportInboxWrite(chatTicketPath(id, "reply"), {
    body,
    setStatus: "open",
    idempotencyKey,
  });
}

export function handChatToAi(id: string): Promise<TicketDetail> {
  return postSupportInboxWrite(chatTicketPath(id, "chat/hand-to-ai"), {});
}

export function sendChatByEmailNow(id: string): Promise<{ sent: boolean }> {
  return postSupportInboxWrite(chatTicketPath(id, "chat/email-now"), {});
}

export function setChatAvailability(
  on: boolean,
): Promise<ChatAvailabilityView> {
  return postSupportInboxWrite("/api/admin/support/chat/availability", { on });
}

export function updateChatSettings(
  settings: Partial<ChatSettings>,
): Promise<ChatSettingsView> {
  return postSupportInboxWrite("/api/admin/support/chat/settings", {
    settings,
  });
}

// --- the staff socket ---------------------------------------------------------

export const CHAT_SOCKET_PATH = "/api/admin/support/chat/ws";

export interface ChatConversationSummary {
  ticketId: string;
  number: number;
  lastSeq: number;
  preview: string;
  owner: ChatOwner;
  status: TicketStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  unreadForStaff: boolean;
  customerOnline: boolean;
  /** Newer nt-support only: the tab is visible. Absent on an older Worker. */
  customerVisible?: boolean;
  aiHandling: boolean;
  requesterKind: "customer" | "visitor";
  updatedAt: string;
  /** The requester's 1-5 score once they rated the chat (never the comment). */
  rating?: number;
}

/** What the console reads off a staff socket (hub or one chat). */
export type StaffFrame =
  | { type: "snapshot"; conversations: ChatConversationSummary[] }
  | { type: "conversation.created"; conversation: ChatConversationSummary }
  | { type: "conversation.updated"; conversation: ChatConversationSummary }
  | { type: "availability"; staff: ChatStaffAvailability[] }
  | ({ type: "msg"; prevSeq?: number } & Omit<
      Extract<ChatHistoryItem, { type: "msg" }>,
      "type"
    >)
  | ({ type: "note" } & Omit<
      Extract<ChatHistoryItem, { type: "note" }>,
      "type"
    >)
  | {
      type: "typing";
      who: { kind: "customer" | "staff" | "ai" | "system"; name: string };
      state: boolean;
    }
  | { type: "replying"; staffName: string; state: boolean }
  | { type: "read"; by: "customer" | "staff"; upToSeq: number }
  | {
      type: "status";
      status: TicketStatus;
      owner: ChatOwner;
      assignee?: { name: string };
    }
  | { type: "welcome"; lastSeq: number; status: TicketStatus; owner: ChatOwner }
  | { type: "reset"; reason: string }
  | {
      type: "customer";
      state: "online" | "away" | "offline";
      lastSeenAt: string | null;
    };

export function socketUrl(location: Location, ticketId?: string): string {
  const url = new URL(CHAT_SOCKET_PATH, location.origin);
  url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
  if (ticketId) url.searchParams.set("ticketId", ticketId);
  return url.toString();
}

/** Full-jitter backoff (spec 8.3): random(0, min(30 s, 0.5 s × 2^n)). */
export function reconnectDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  return Math.floor(random() * Math.min(30_000, 500 * 2 ** attempt));
}

export function parseStaffFrame(data: unknown): StaffFrame | null {
  if (typeof data !== "string" || data === "pong") return null;
  try {
    const frame = JSON.parse(data) as { type?: unknown };
    return frame && typeof frame.type === "string"
      ? (frame as StaffFrame)
      : null;
  } catch {
    return null;
  }
}

// --- pure rules ------------------------------------------------------------------

/** A live row joins the page in seq order; a row already there is replaced. */
export function mergeChatItem(
  page: ChatHistoryPage,
  item: ChatHistoryItem,
): ChatHistoryPage {
  const items = page.items.filter((existing) => existing.seq !== item.seq);
  items.push(item);
  items.sort((a, b) => a.seq - b.seq);
  const lastSeq = Math.max(page.lastSeq, item.seq);
  return {
    ...page,
    items,
    lastSeq,
    chat: { ...page.chat, lastSeq: Math.max(page.chat.lastSeq, item.seq) },
  };
}

/**
 * An older page (read with `beforeSeq`) joined in front of the rows on
 * screen: rows already there win, and `hasMore` is the older page's word on
 * whether anything is left before it.
 */
export function mergeOlderPage(
  page: ChatHistoryPage,
  older: ChatHistoryPage,
): ChatHistoryPage {
  const shown = new Set(page.items.map((item) => item.seq));
  const items = [
    ...older.items.filter((item) => !shown.has(item.seq)),
    ...page.items,
  ].sort((a, b) => a.seq - b.seq);
  return { ...page, items, hasMore: older.hasMore };
}

/** A socket frame as a history row, or null when it is not one. */
export function frameItem(frame: StaffFrame): ChatHistoryItem | null {
  if (frame.type === "msg") {
    const { type: _type, prevSeq: _prevSeq, ...message } = frame;
    return { type: "msg", ...message };
  }
  if (frame.type === "note") {
    const { type: _type, ...note } = frame;
    return { type: "note", ...note };
  }
  return null;
}

export type OwnerChipTone = "ai" | "waiting" | "person" | "nobody";

/**
 * Who has a chat, as the list's chip reads: the AI, a person by name, or
 * "Needs reply" when it waits for a human nobody has taken yet (not
 * "Waiting", which is the ticket status meaning the customer owes us).
 */
export function chatOwnerChip(ticket: {
  chat?: TicketSummary["chat"];
  assigneeName: string | null;
  awaitingReply: boolean;
  status: TicketStatus;
}): { label: string; tone: OwnerChipTone } | null {
  const chat = ticket.chat;
  if (!chat) return null;
  if (chat.owner === "ai") return { label: "AI", tone: "ai" };
  if (ticket.assigneeName) {
    return { label: ticket.assigneeName.split(/\s+/)[0]!, tone: "person" };
  }
  if (
    ticket.awaitingReply &&
    (ticket.status === "open" || ticket.status === "waiting")
  ) {
    return { label: "Needs reply", tone: "waiting" };
  }
  return null;
}

export interface ChatAlert {
  ticketId: string;
  title: string;
  body: string;
}

/**
 * Whether a hub update should ring: a new chat, or a new customer message on
 * a chat assigned to me or waiting for a human. The AI's own chats and my
 * colleagues' chats stay quiet.
 */
export function chatAlertFor(
  previous: ChatConversationSummary | undefined,
  next: ChatConversationSummary,
  meId: string | null,
  created: boolean,
): ChatAlert | null {
  if (next.status !== "open" && next.status !== "waiting") return null;
  const label = `T-${String(next.number).padStart(5, "0")}`;
  const aiHasIt = next.aiHandling || next.owner === "ai";
  // A chat missing from the hub's capped snapshot is not new: one the AI
  // has stays quiet like any other AI chat.
  if (created || (!previous && !aiHasIt)) {
    return {
      ticketId: next.ticketId,
      title: "New chat",
      body: next.preview || label,
    };
  }
  if (!previous) return null;
  const newCustomerMessage =
    next.lastSeq > previous.lastSeq &&
    next.unreadForStaff &&
    (!previous.unreadForStaff || next.preview !== previous.preview);
  if (!newCustomerMessage || aiHasIt) return null;
  const mine = meId !== null && next.assigneeId === meId;
  const waitingForHuman = next.assigneeId === null;
  if (!mine && !waitingForHuman) return null;
  return {
    ticketId: next.ticketId,
    title: mine ? `New message in ${label}` : `${label} is waiting for a reply`,
    body: next.preview || label,
  };
}

/** Open or waiting chats with a customer message no one has read. */
export function unreadChatCount(
  summaries: Iterable<ChatConversationSummary>,
): number {
  let count = 0;
  for (const summary of summaries) {
    if (
      summary.unreadForStaff &&
      (summary.status === "open" || summary.status === "waiting")
    ) {
      count += 1;
    }
  }
  return count;
}

const CHAT_COUNT_PREFIX = /^\(\d+ chats?\) /;

/** The tab title with "(2 chats) " in front, or without it when none is unread. */
export function withChatCount(title: string, count: number): string {
  const base = title.replace(CHAT_COUNT_PREFIX, "");
  if (count <= 0) return base;
  return `(${count} ${count === 1 ? "chat" : "chats"}) ${base}`;
}

// --- customer presence ---------------------------------------------------------------

export type CustomerPresence = "online" | "away" | "offline";

/**
 * The customer's presence from a chat read or a hub row. nt-support's newer
 * `customerState` wins; an older Worker only sends `customerOnline`.
 */
export function customerPresence(chat: {
  customerOnline: boolean;
  customerState?: CustomerPresence;
  customerLastSeenAt?: string | null;
}): { state: CustomerPresence; lastSeenAt: string | null } {
  return {
    state: chat.customerState ?? (chat.customerOnline ? "online" : "offline"),
    lastSeenAt: chat.customerLastSeenAt ?? null,
  };
}

/** A hub row's presence: online with a hidden tab is "away". */
export function summaryPresence(
  summary: Pick<ChatConversationSummary, "customerOnline" | "customerVisible">,
): CustomerPresence {
  if (!summary.customerOnline) return "offline";
  return summary.customerVisible === false ? "away" : "online";
}

function lastSeenText(lastSeenAt: string | null, now: Date): string | null {
  if (!lastSeenAt) return null;
  const seen = new Date(lastSeenAt);
  const elapsed = now.getTime() - seen.getTime();
  if (Number.isNaN(elapsed)) return null;
  if (elapsed < 60_000) return "just now";
  if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(seen.getHours())}:${pad(seen.getMinutes())}`;
}

/** "Customer online", "Customer away · last seen 2m ago", "Customer offline · last seen 14:03". */
export function presenceLabel(
  state: CustomerPresence,
  lastSeenAt: string | null,
  now: Date = new Date(),
): string {
  if (state === "online") return "Customer online";
  const seen = lastSeenText(lastSeenAt, now);
  const base = state === "away" ? "Customer away" : "Customer offline";
  return seen ? `${base} · last seen ${seen}` : base;
}

/** The open ticket's chat read with a new presence applied. */
export function withCustomerPresence(
  page: ChatHistoryPage,
  presence: {
    state: CustomerPresence;
    lastSeenAt: string | null;
    customerOnline?: boolean;
  },
): ChatHistoryPage {
  return {
    ...page,
    chat: {
      ...page.chat,
      customerOnline: presence.customerOnline ?? presence.state === "online",
      customerState: presence.state,
      customerLastSeenAt: presence.lastSeenAt,
    },
  };
}

// --- the in-app toast -------------------------------------------------------------------

/** How long a customer-message toast stays; a new chat stays until closed. */
export const MESSAGE_TOAST_MS = 8_000;

export function chatToastFor(alert: ChatAlert, created: boolean) {
  return {
    title: alert.title,
    description: alert.body.slice(0, 120),
    type: "info" as const,
    timeout: created ? 0 : MESSAGE_TOAST_MS,
    ticketId: alert.ticketId,
  };
}

// --- business hours as text --------------------------------------------------------

export const WEEKDAYS: readonly ChatWeekday[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export const WEEKDAY_LABEL: Record<ChatWeekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** "09:00-12:00, 13:00-17:00" from stored ranges; "" for a closed day. */
export function hoursText(ranges: Array<[string, string]> | undefined): string {
  return (ranges ?? []).map(([start, end]) => `${start}-${end}`).join(", ");
}

/** Ranges from "09:00-12:00, 13:00-17:00"; "" is a closed day. */
export function parseHoursText(
  text: string,
):
  | { ok: true; ranges: Array<[string, string]> }
  | { ok: false; message: string } {
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");
  if (parts.length > 3) {
    return { ok: false, message: "Use at most three ranges a day." };
  }
  const ranges: Array<[string, string]> = [];
  for (const part of parts) {
    const [start, end, extra] = part.split("-").map((value) => value.trim());
    if (
      extra !== undefined ||
      !start ||
      !end ||
      !HHMM.test(start) ||
      !HHMM.test(end) ||
      start >= end
    ) {
      return {
        ok: false,
        message: `"${part}" is not a range. Write it as 09:00-17:00 (24-hour, start before end).`,
      };
    }
    ranges.push([start, end]);
  }
  return { ok: true, ranges };
}
